import { createContext, useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';

import { AuthLoading } from '@app/auth/_components/AuthLoading';
import { Unauthorized } from '@app/auth/_components/Unauthorized';
import { SoftphoneSession } from '@components/contacts/sessions/SoftphoneSession';
import { pattern } from '@endurancewarranty/lib';
import { useQueryGetMyUser } from '@hooks/query/users/useQueryGetMyUser';
import { useIsPopupWindow } from '@hooks/useIsPopupWindow';
import { signInAction } from '@lib/actions';
import { SoftphoneConnectionBehaviorEnum, TimeEnum } from '@lib/enums';
import { appAtom } from '@states/app/appAtom';
import { useSetJotaiAtom } from '@states/jotai/hooks';
import { softphoneAtom } from '@states/softphone/softphoneAtom';

import type { TDashboardUx } from '@lib/types/generated/TPermission';
import type { TUser } from '@models/authorization/TUser';
import type { FC, ReactNode } from 'react';

const { match, isMatching, P } = pattern;

/**
 * Value exposed by UserContext.
 * Guaranteed to contain user once provider renders children.
 **/
export type TUserState = Readonly<{
  user: TUser;
}>;

/**
 * Provides authenticated user.
 */
export const UserContext = createContext<TUserState | null>(null);

type Props = {
  children: ReactNode;
};

/**
 * Provider for post-auth setup.
 *
 * Lifecycle:
 * 1. Loading.
 * 2. Auth check, redirects unauthenticated users.
 * 3. User fetch, calls API to hydrate full `TUser`.
 * 4. Role setup, derive dashboard visibility + softphone behavior from roles.
 * 5. Ready.
 */
export const UserContextProvider: FC<Props> = ({ children }) => {
  const { data: session, status, update } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const isPopupWindow = useIsPopupWindow();
  const [isAppReady, setIsAppReady] = useState(false);

  const setAppAtom = useSetJotaiAtom(appAtom);
  const setSoftphoneAtom = useSetJotaiAtom(softphoneAtom);

  const [refreshRetried, setRefreshRetried] = useState(false);

  // On RefreshTokenError, force one immediate session re-fetch before showing sign-in screen.
  useEffect(() => {
    if (session?.error === 'RefreshTokenError' && !refreshRetried) {
      setRefreshRetried(true);
      update().catch(() => {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Session update() rejected during RefreshTokenError retry',
          level: 'warning',
        });
      });
    }
    if (session?.error == null) {
      setRefreshRetried(false);
    }
  }, [session?.error, refreshRetried, update]);

  const isAuthenticated = status === 'authenticated';
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useQueryGetMyUser({ enabled: isAuthenticated });

  useEffect(() => {
    if (user == null || isPopupWindow) {
      setSoftphoneAtom((current) => ({
        ...current,
        connectionBehavior: SoftphoneConnectionBehaviorEnum.DoNotConnect,
      }));

      setIsAppReady(true);

      return;
    }

    const hasRole = (key: string): boolean => user.roles.some(isMatching({ roleKey: key }));

    const isSuperAdmin = hasRole('super-admin');
    const isBusinessAdmin = hasRole('business-admin');
    const isReadOnly = hasRole('read-only');
    const isSalesLeaderRole = user.roles.some(
      isMatching({
        roleKey: P.union('sales-manager', 'sales-director'),
      }),
    );
    const isCustomerServiceManagerRole = hasRole('customer-service-manager');
    const isCustomerServiceRole = hasRole('customer-service');
    const isQualityAssuranceRole = hasRole('quality-assurance');
    const isClaimsAdjuster = hasRole('claims-adjuster');
    const isBillingLeadershipRole = hasRole('billing-leadership');
    const isPaymentsManager = hasRole('payments-manager');
    const isRetentionManager = hasRole('retention-manager');
    const isResolutions = hasRole('resolutions');

    const items = [
      ...new Set(user.v2?.roles.map((role) => role.dashboardUx) ?? []),
    ] as TDashboardUx[];

    const connectionBehavior = match({
      isReadOnly,
      isSalesLeaderRole,
      isSuperAdmin,
      isBusinessAdmin,
      isCustomerServiceManagerRole,
      isCustomerServiceRole,
      isQualityAssuranceRole,
      isClaimsAdjuster,
      isBillingLeadershipRole,
      isPaymentsManager,
      isRetentionManager,
      isResolutions,
    })
      .with(
        { isSalesLeaderRole: true },
        { isCustomerServiceManagerRole: true },
        { isSuperAdmin: true },
        () => SoftphoneConnectionBehaviorEnum.OptIn,
      )
      .with(
        { isReadOnly: true },
        { isBusinessAdmin: true },
        { isQualityAssuranceRole: true },
        { isClaimsAdjuster: true },
        { isBillingLeadershipRole: true },
        { isPaymentsManager: true },
        { isRetentionManager: true },
        { isResolutions: true },
        () => SoftphoneConnectionBehaviorEnum.DoNotConnect,
      )
      .otherwise(() => SoftphoneConnectionBehaviorEnum.AutoConnect);

    setSoftphoneAtom((curr) => ({ ...curr, connectionBehavior }));
    setAppAtom((curr) => ({
      ...curr,
      dashboard: items,
    }));

    setIsAppReady(true);
  }, [setSoftphoneAtom, setAppAtom, isPopupWindow, user]);

  // Without this, unauthenticated user outside /auth see "Redirecting to login..." forever.
  useEffect(() => {
    if (status === 'unauthenticated' && !pathname?.startsWith('/auth')) {
      const params = new URLSearchParams();

      if (pathname && pathname !== '/') {
        const search = window.location.search;
        params.set('callbackUrl', search ? `${pathname}${search}` : pathname);
      }

      const query = params.toString();
      const target = query ? `/auth?${query}` : '/auth';

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Redirecting unauthenticated user to /auth',
        level: 'warning',
        data: { from: pathname, to: target },
      });

      router.replace(target);
    }
  }, [status, pathname, router]);

  useEffect(() => {
    if (user != null) {
      const sentryUser = {
        id: user.azureAdId,
        email: user.emailAddress.toLowerCase(),
      };
      Sentry.setUser(sentryUser);
    }

    return () => Sentry.setUser(null);
  }, [user]);

  useEffect(() => {
    if (userError) {
      Sentry.captureException(userError);
    }
  }, [userError]);

  if (status === 'loading') {
    return <AuthLoading reason="Authenticating..." />;
  }

  const fullCallbackUrl = (() => {
    if (!pathname) return undefined;
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return search ? `${pathname}${search}` : pathname;
  })();

  if (session?.error === 'RefreshTokenError') {
    if (!refreshRetried) {
      return <AuthLoading reason="Authenticating..." />;
    }

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Session has RefreshTokenError — showing sign-in prompt',
      level: 'warning',
      data: { pathname, hasAccessToken: !!session.accessToken },
    });

    return (
      <Unauthorized
        action={signInAction}
        buttonLabel="Sign in"
        callbackUrl={fullCallbackUrl}
        message={
          <>
            Your session has expired.
            <br />
            Please sign in again to continue.
          </>
        }
      />
    );
  }

  if (!isAuthenticated) {
    if (pathname?.startsWith('/auth')) {
      return <>{children}</>;
    }
    return (
      <AuthLoading
        reason="Redirecting to login"
        fallbackAction={signInAction}
        fallbackCallbackUrl={fullCallbackUrl}
        fallbackDelayMs={TimeEnum.FiveSeconds}
      />
    );
  }

  if (isUserLoading) {
    return <AuthLoading reason="Fetching account" />;
  }

  if (isUserError) {
    return (
      <Unauthorized
        action={signInAction}
        buttonLabel="Try again"
        callbackUrl={fullCallbackUrl}
        message={
          <>
            Something went wrong during sign in.
            <br />
            Please try again or contact IT if the problem continues.
          </>
        }
      />
    );
  }

  if (!isAppReady) {
    return <AuthLoading reason="Setting up KITT" />;
  }

  return (
    <UserContext.Provider value={{ user: user as TUser }}>
      {isPopupWindow ? children : <SoftphoneSession>{children}</SoftphoneSession>}
    </UserContext.Provider>
  );
};
